import { useEffect, useMemo } from 'react'
import Router from '@/router'
import {
  EnvironmentIdentifier,
  ThemeProvider,
  ToastContainer,
} from '@conexasaude/conexa-hero'
import { QueryClientProvider, QueryClient } from '@tanstack/react-query'
import { Global } from '@emotion/react'
import { useGlobalStyles } from '@/styles/global'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { MedicalSpecialitiesProvider } from '@/modules/MedicalSpecialities/contexts/MedicalSpecialitiesContext'
import { ModalProvider } from '@/components/Modal/contexts/ModalContext'
import { AttendanceProvider } from '@/modules/Attendance/contexts/AttendanceContex'
import { CheckoutProvider } from '@/modules/Checkout/contexts/CheckoutContext'
import ScreeningProvider from '@/modules/Screening/contexts/screeningContext'
import { RealtimeAttendanceProvider } from '@/modules/RealtimeAttendance/contexts/RealtimeAttendanceContext'
import { FirebaseProvider } from '@/modules/Firebase/contexts/FirebaseContext'
import RealtimeAttendance from '@/modules/RealtimeAttendance/pages'
// import { Zendesk } from '@/components/Zendesk'
import { BenefitsProvider } from './modules/Benefits/contexts/BenefitsContext'
import { ConciergeAps } from '@/modules/HealthTeams/components/ConciergeAps'
import { RenderIf } from './components/RenderIf'
import { useAuthContext } from './modules/Auth/contexts/AuthContext'
import { GroupConsultationProvider } from './modules/MyCalls/contexts/consultationGroupContext'
import { startBrazeSdk } from './services/braze'
// import { ChatLayer } from './components/ChatLayer'
import { GlobalActions } from './components/GlobalActions'
import useHeight from './utils/useHeight'
import browserUpdate from 'browser-update'
import { AnalyticsProvider } from '@conexasaude/analytics'
import { ReservationConsultationProvider } from './modules/MyCalls/contexts/consultationReservationContext'
import { IS_PRODUCTION } from './constants'
import { ProfessionalPsyAliveProvider } from './modules/Attendance/submodules/ScheduledPsyAlive/PsychologistContext'
import * as device from 'react-device-detect'
import './services/sentry'
import { IconographyConfig } from '@conexasaude/iconography/utils'
import { BrowserRouter } from 'react-router-dom'

// Needed for hero-web toast
import 'animate.css'
import { useAnalyticsData } from '@/hooks/useAnalyticsData'
import { FunctionComponentChildren } from './@types/react'
import { RealtimeAttendanceMonitoring } from './modules/RealtimeAttendanceMonitoring/pages'
import '@conexasaude/conexa-hero/styles.css'
import { setupInsiderSdk } from './services/insider'
import { BiometryManager } from './modules/Auth/submodules/Biometry/pages'

const queryClient = new QueryClient()

const App: FunctionComponentChildren = () => {
  const { theme } = useThemeGeneratorContext()
  const { user } = useAuthContext()
  const analyticsDataSync = useAnalyticsData(user)
  const height = useHeight()
  const hasHealthTeamEnabled = useMemo(() => user?.isHealthTeam, [user])

  const themeConexaHero = useMemo(() => {
    if (device.isMobile) {
      return theme?.theme?.mobile
    }

    return theme?.theme?.desktop
  }, [theme?.theme?.desktop, theme?.theme?.mobile])

  const styles = useGlobalStyles(themeConexaHero, { height })

  useEffect(() => {
    if (!user) return

    startBrazeSdk(user)
    setupInsiderSdk(user)
  }, [user])

  useEffect(() => {
    browserUpdate({
      required: {
        c: -1,
        f: -1,
        s: -1,
        e: -1,
        e_a: -1,
        i: -1,
        ios: -1,
        samsung: -1,
        o: -1,
        o_a: -1,
        y: -1,
        v: -1,
        uc: -1,
        a: -1,
        silk: -1,
        waterfox: -1,
        palemoon: -1,
      },
      // insecure: true,
      // unsupported: true,
      reminder: 24,
      reminderClosed: 48,
      // no_permanent_hide: true,

      text: {
        msg: 'Seu navegador da web ({brow_name}) está desatualizado.',
        msgmore:
          'Atualize seu navegador para mais segurança, velocidade e melhor experiência deste site',
        bupdate: 'Atualizar navegador',
        bignore: 'Agora não',
        remind: 'Você será lembrado novamente em {days} dias.',
        bnever: 'Nunca mostrar novamente',
      },
    })
  }, [])

  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider
          theme={themeConexaHero}
          fonts={themeConexaHero?.fonts}
          setterTheme={IconographyConfig.updateTheme}
        >
          <MedicalSpecialitiesProvider>
            <AttendanceProvider>
              <CheckoutProvider>
                <ModalProvider>
                  <RealtimeAttendanceProvider>
                    <ScreeningProvider>
                      <AnalyticsProvider appDataSync={analyticsDataSync}>
                        <FirebaseProvider>
                          <BenefitsProvider>
                            <GroupConsultationProvider>
                              <ProfessionalPsyAliveProvider>
                                <ReservationConsultationProvider>
                                  <div className="environmentIdentifier">
                                    <EnvironmentIdentifier
                                      disabled={IS_PRODUCTION}
                                    />
                                  </div>
                                  <div
                                    id="app"
                                    css={[
                                      styles.mainContainer,
                                      styles.typography,
                                    ]}
                                  >
                                    <Router />
                                    <RealtimeAttendance />
                                    <BiometryManager />
                                    <RealtimeAttendanceMonitoring />
                                    <div id="modal-root" />
                                    <div id="hero-modal-root" />
                                    <RenderIf condition={hasHealthTeamEnabled}>
                                      <ConciergeAps />
                                    </RenderIf>
                                    {/* <Zendesk />
                                <ChatLayer /> */}
                                    <GlobalActions />
                                  </div>
                                </ReservationConsultationProvider>
                              </ProfessionalPsyAliveProvider>
                            </GroupConsultationProvider>
                          </BenefitsProvider>
                        </FirebaseProvider>
                      </AnalyticsProvider>
                    </ScreeningProvider>
                  </RealtimeAttendanceProvider>
                </ModalProvider>
              </CheckoutProvider>
              <ToastContainer position="top-right" />
              <Global styles={styles.global} />
            </AttendanceProvider>
          </MedicalSpecialitiesProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  )
}

export default App
